import {
  useHistory,
  useLocation,
} from 'react-router-dom';
import { useState } from 'react';
import classNames from 'classnames';
import { t } from 'ttag';

import { FeedAside } from '@components/FeedAside';
import { useInfinityScroll } from '@hook/useInfinityScroll';
import { getSearchText, removeSearchTextFilter } from '@utils/myProducts';
import { TopCategory } from '@components/FeedCatalogNav';
import { Button } from '@ui/Button';
import FilterIcon from '@svg/ic_filter.svg';
import { SortSelect } from '@components/SortSelect';
import { ActiveFilters } from '@components/ActiveFilters';
import { ActiveFilter } from '@ui/ActiveFilter';
import NoProductsPlaceholder from '@components/NoProductsPlaceholder';
import { Grid } from '@components/Grid';
import { ProductCard, Product } from '@components/ProductCard';
import ViewerProfileFeedPreloader from '@components/ViewerProfileFeedPreloader';
import {
  Breadcrumb,
  Breadcrumbs,
} from '@components/catalog/Breadcrumbs';
import { RichText } from '@ui/RichText';
import * as path from '@legacyUrls';
import { Title } from '@ui/Title';
import {
  LinkedCatalogLink,
  VirtualCatalogLinkedCatalogs,
} from '@components/catalog/VirtualCatalogLinkedCatalogs';
import { CatalogSiblings, Sibling } from '@components/catalog/CatalogSiblings';
import { setProductReferrerPathnameToStorage } from '@utils/common';

import s from './VirtualCatalog.sss';

type VirtualCatalogProps = {
  title: string;
  products: Product[];
  hasNextPage: boolean;
  loading: boolean;
  categories: TopCategory[] | null;
  total: number;
  mostFrequentCatalogSlug?: string | null
  onLoadMore(): void;
  seoDescription?: string | null
  breadcrumbs: Breadcrumb[]
  siblings: Sibling[]
  linkedCatalogs: LinkedCatalogLink[]
  className?: string
};

export const VirtualCatalog: React.FC<VirtualCatalogProps> = ({
  title,
  products,
  onLoadMore,
  hasNextPage,
  total,
  loading,
  mostFrequentCatalogSlug,
  seoDescription,
  breadcrumbs,
  siblings,
  linkedCatalogs,
  className,
}) => {
  const ref = useInfinityScroll(onLoadMore, 1000);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const searchText = getSearchText(location.search);

  return (
    (
      <div ref={ref} className={classNames(s.root, className)}>
        <div className={s.feedWrapper}>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <FeedAside
            total={total}
            isMobile={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            isSizeFilterEnable={true}
            isBrandFilterEnable={true}
            isColorFilterEnable={true}
            isPriceFilterEnable={true}
            isConditionFilterEnable={true}
            sizeCatalogSlug={mostFrequentCatalogSlug}
          />
        </div>
        <div className={classNames(s.rightBlockWrapper, { [s.loading]: loading })}>
          <div className={s.mobileSortingFiltering}>
            <SortSelect className={s.sort} />
            <Button
              className={s.buttonFilter}
              onClick={() => setIsFilterOpen(true)}
            >
              <FilterIcon className={s.filterButtonIcon} />
              <span className={s.filterText}>{t`Фільтри`}</span>
            </Button>
          </div>

          <Title type="h1" bold={false} className={s.title}>{title}</Title>

          <VirtualCatalogLinkedCatalogs
            className={s.linkedCatalogs}
            linkedCatalogs={linkedCatalogs}
          />

          <div className={s.desktopSortWrapper}>
            <SortSelect className={s.sort} />
          </div>

          <div className={s.activeFilters}>
            <ActiveFilters sizeCatalogSlug={mostFrequentCatalogSlug} />
            {searchText && (
            <ActiveFilter onClick={() => removeSearchTextFilter(location.search, history)}>
              {searchText}
            </ActiveFilter>
            )}
          </div>
          {!products.length && (
          <NoProductsPlaceholder
            className={s.notFound}
            title={t`Не знайдено жодної речі на Шафі`}
            subTitle={t`Давайте пошукаємо щось інше...`}
            action={(
              <Button
                className={s.notFoundButton}
                href={path.mainPage}
              >
                {t`На головну`}
              </Button>
            )}
          />
          )}
          <Grid container={true} spacing={10} className={s.grid}>
            {
            products.map((p) => (
              <Grid
                key={p.id}
                desktop={3}
                tablet={4}
                def={6}
              >
                <ProductCard
                  {...p}
                  onProductClick={() => {
                    setProductReferrerPathnameToStorage({
                      productId: p.id,
                      pathname: location.pathname,
                    });
                  }}
                />
              </Grid>
            ))
          }
          </Grid>
          {hasNextPage && <ViewerProfileFeedPreloader />}

          <CatalogSiblings siblings={siblings} />

          {seoDescription && <RichText dangerouslySetInnerHTML={{ __html: seoDescription }} />}
        </div>
      </div>
    )
  );
};
